/**
 * Labstep
 *
 * @module components/FilterPost/NewIndex/Entity/Active/Post
 * @desc Active node component for entities
 */

import type { FilterPostActiveLabelProps } from 'labstep-web/components/FilterPost/Active/Label/types';
import { FilterPostNewIndexEntity } from 'labstep-web/components/FilterPost/NewIndex/Entity';
import { getPostFilterEntity } from 'labstep-web/components/FilterPost/NewIndex/Entity/Form';
import { ICONS } from 'labstep-web/constants/icons';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { RemoveLabel } from 'labstep-web/core/Label/Remove';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadableShort,
} from 'labstep-web/services/postFilter/constants';
import type React from 'react';

export const FilterPostNewIndexEntityActivePost: React.FC<
  FilterPostActiveLabelProps
> = ({ node, onRemoveClick }) => {
  const PostFilterPathToEntityName = {
    'entityUsers.user': User.entityName,
  };

  const FILTERS = [
    getPostFilterEntity(
      'guid',
      PostFilterComparison.eq,
      'entityUsers.user',
    ),
  ];

  const { addPostFilter } = useParamsContext();
  const filterActive = FILTERS.find((filter) =>
    filter.isNodeActive(node),
  );

  if (!filterActive) {
    return null;
  }

  const path = filterActive.getAllPaths()[0] || '';

  let attributes = filterActive.getAttributeValues(node);
  const protocolValueName = attributes.name?.value;

  if (filterActive.getPredicates().length > 1) {
    attributes = Object.fromEntries(
      Object.entries(filterActive.getAttributeValues(node)).filter(
        ([key, value]) => key !== 'name',
      ),
    );
  }

  const entityName =
    PostFilterPathToEntityName[
      path as keyof typeof PostFilterPathToEntityName
    ];

  let label = getHumanReadableEntityName(entityName, false, true);

  const icon = ICONS[entityName as keyof typeof ICONS]?.primary;

  const comparison = PostFilterComparison.eq;
  const value = Object.values(attributes)[0].value;

  const comparaisonReadable =
    PostFilterComparisonHumanReadableShort[
      comparison as keyof typeof PostFilterComparisonHumanReadableShort
    ];

  const attributeName = Object.keys(attributes)[0];

  const params: Record<string, any> = {
    get_single: 1,
  };

  params[attributeName] = value;

  if (entityName === User.entityName) {
    label = 'Collaborator ';
  }

  const comparatorReadable = `${label} ${comparaisonReadable}`;

  return (
    <ReadOnMountHOC
      type="entities"
      entityName={entityName}
      params={params}
      loading={{ loader: 'show', cached: true }}
      children={({ entity }) => (
        <FilterPostNewIndexEntity
          label={label}
          path={path}
          field={'id'}
          addPostFilter={addPostFilter}
          defaultValues={{}}
          viewComponent={({ toggleModal }) => (
            <RemoveLabel
              name={`${comparatorReadable}: ${entity.name}`}
              icon={icon}
              onRemoveClick={onRemoveClick}
            />
          )}
          entityName={entityName}
        />
      )}
    />
  );
};
